import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import JordanMap from '../components/JordanMap';
import ExpertList from '../components/ExpertList';
import SearchFilters from '../components/SearchFilters';
import ExpertModal from '../components/ExpertModal';
import { useExpertContext } from '../context/ExpertContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiMapPin, FiBookOpen } = FiIcons;

const HomePage = () => {
  const { experts, filteredExperts, searchExperts } = useExpertContext();
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleExpertSelect = (expert) => {
    setSelectedExpert(expert);
    setShowModal(true);
  };

  const stats = [
    {
      icon: FiUsers,
      label: 'Total Experts',
      value: experts.filter(e => e.status === 'approved').length,
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    {
      icon: FiMapPin,
      label: 'Governorates',
      value: new Set(experts.filter(e => e.status === 'approved').map(e => e.governorate)).size,
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    {
      icon: FiBookOpen,
      label: 'Support Topics',
      value: new Set(experts.flatMap(e => e.support_topics || [])).size,
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    }
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Hero Section */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="text-center mb-8"
      >
        <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-4">
          Minecraft Education Experts in Jordan
        </h1>
        <p className="text-xl text-gray-600 mb-6 max-w-2xl mx-auto">
          Connect with experienced Minecraft Education teachers across Jordan. 
          Find mentors, share knowledge, and build collaborative learning communities.
        </p>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              className="expert-card text-center"
            >
              <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                <SafeIcon icon={stat.icon} className={`text-2xl ${stat.color}`} />
              </div>
              <div className="text-3xl font-bold text-gray-800">{stat.value}</div>
              <div className="text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Search and Filters */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mb-6"
      >
        <SearchFilters onSearch={searchExperts} />
      </motion.div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-2"
        >
          <div className="expert-card h-96 lg:h-[500px]">
            <JordanMap 
              experts={filteredExperts} 
              onExpertSelect={handleExpertSelect}
            />
          </div>
        </motion.div>

        {/* Expert List */}
        <motion.div
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <ExpertList 
            experts={filteredExperts} 
            onExpertSelect={handleExpertSelect}
          />
        </motion.div>
      </div>

      {/* Expert Modal */}
      {showModal && selectedExpert && (
        <ExpertModal
          expert={selectedExpert}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default HomePage;